/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable react-refresh/only-export-components */
import { useState, useEffect, createContext, useContext } from "react";
import type { User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { logger } from "@/lib/logger";

interface Profile {
  id: string;
  email: string;
  full_name: string | null;
  role: "client" | "therapist";
  assigned_journey_id: string | null;
  current_week: number | null;
  current_session: number | null;
  unlocked_sessions: number | null;
  onboarding_completed: boolean;
  journey_start_date: string | null;
  journey_length_weeks: number;
  requires_password_setup?: boolean;
}

interface AuthContextType {
  user: User | null;
  profile: Profile | null;
  loading: boolean;
  signOut: () => Promise<void>;
  refetchProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    logger.debug("AuthProvider useEffect - setting up auth listener");

    // Listen for auth changes FIRST (prevent missing events during initialization)
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      logger.debug("Auth state changed:", {
        event,
        email: session?.user?.email,
      });

      setUser(session?.user ?? null);

      if (session?.user) {
        // Defer profile fetching to avoid blocking auth state change
        setTimeout(() => {
          fetchProfile(session.user.id);
        }, 0);
      } else {
        setProfile(null);
        setLoading(false);
      }
    });

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session }, error }) => {
      logger.debug("Initial session:", { email: session?.user?.email, error });
      setUser(session?.user ?? null);

      if (session?.user) {
        fetchProfile(session.user.id);
      } else {
        setProfile(null);
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const fetchProfile = async (userId: string) => {
    logger.debug("Fetching profile for user:", { userId });
    try {
      // Support different mock shapes in tests: some return a chain with maybeSingle/single,
      // others resolve directly from eq(). We handle both gracefully.
      const baseQuery: any = supabase
        .from("profiles")
        .select("*")
        .eq("id", userId);

      let result: any;
      if (typeof baseQuery.maybeSingle === "function") {
        result = await baseQuery.maybeSingle();
      } else if (typeof baseQuery.single === "function") {
        result = await baseQuery.single();
      } else if (typeof baseQuery.then === "function") {
        // If the mock returns a thenable at this point
        result = await baseQuery;
      } else {
        // Fallback to a safe default
        result = { data: null, error: null };
      }

      const { data, error } = result;

      if (error) {
        logger.error("Error fetching profile:", error);
        setProfile(null);
        return;
      }

      if (!data) {
        logger.error("No profile found for user:", { userId });
        setProfile(null);
        return;
      }

      logger.debug("Profile fetched successfully:", {
        email: data.email,
        role: data.role,
      });
      // Type assertion to ensure role is properly typed
      const profileData: Profile = {
        ...data,
        role: data.role as "client" | "therapist",
      };
      setProfile(profileData);

      // Check if user needs to setup password (for newly invited users)
      if (data.requires_password_setup) {
        logger.debug("User requires password setup, redirecting...");
        // This will be handled by the route component
      }
    } catch (error) {
      logger.error("Unexpected error fetching profile:", error);
      setProfile(null);
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    setLoading(true);
    await supabase.auth.signOut();
    setUser(null);
    setProfile(null);
    setLoading(false);
  };

  const refetchProfile = async () => {
    if (user?.id) {
      await fetchProfile(user.id);
    }
  };

  return (
    <AuthContext.Provider
      value={{ user, profile, loading, signOut, refetchProfile }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
